/**
 * PayLink — public payment landing page for /pay/{code}.
 * No authentication required. Renders a branded Stripe checkout redirect.
 */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, CheckCircle2, Lock, Loader2, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function money(n) { return `£${(Number(n) || 0).toFixed(2)}`; }

export default function PayLink() {
  const { code } = useParams();
  const [link, setLink] = useState(null);
  const [status, setStatus] = useState("loading");
  const [processing, setProcessing] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/pay/${code}`);
        setLink(data);
        setStatus(data.status);
      } catch (e) {
        setStatus("error");
        setErr(e?.response?.data?.detail || "Payment link not found or expired");
      }
    })();
  }, [code]);

  const startCheckout = async () => {
    setProcessing(true);
    try {
      const { data } = await axios.post(`${API}/pay/${code}/checkout`, {}, {
        headers: { Origin: window.location.origin },
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not start payment. Please try again.");
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="pay-link-page">
      <div className="w-full max-w-md">
        {/* Brand header */}
        <div className="flex items-center gap-2.5 justify-center mb-6">
          <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
            <ShieldCheck className="w-4 h-4 text-secondary" />
          </div>
          <div>
            <div className="font-display font-semibold text-lg leading-none">Treasurescare</div>
            <div className="text-[10px] overline text-muted-foreground">Secure payment</div>
          </div>
        </div>

        {status === "loading" && (
          <Card><CardContent className="pt-6 text-center py-12">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
          </CardContent></Card>
        )}

        {status === "error" && (
          <Card><CardContent className="pt-6 text-center py-12" data-testid="pay-link-error">
            <AlertCircle className="w-10 h-10 mx-auto text-destructive mb-3" />
            <div className="font-medium">Payment link unavailable</div>
            <p className="text-sm text-muted-foreground mt-1">{err}</p>
            <Button asChild className="mt-4"><a href="/">Return to Treasurescare</a></Button>
          </CardContent></Card>
        )}

        {status === "paid" && (
          <Card><CardContent className="pt-6 text-center py-12" data-testid="pay-link-paid">
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500 mb-3" />
            <div className="font-display text-xl">Payment received</div>
            <p className="text-sm text-muted-foreground mt-1">Thanks — your payment of {money(link?.amount_gbp)} was successful.</p>
            <div className="mt-4 text-xs text-muted-foreground">
              A confirmation has been sent to your email. Reference: <span className="font-mono">{code}</span>
            </div>
          </CardContent></Card>
        )}

        {status === "open" && link && (
          <Card data-testid="pay-link-open">
            <CardContent className="pt-6 space-y-5">
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground overline">Amount due</div>
                <div className="text-4xl font-mono font-light mt-1" data-testid="pay-link-amount">{money(link.amount_gbp)}</div>
              </div>
              <div className="border-t border-border pt-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">For</span><span className="font-medium text-right">{link.description}</span></div>
                {link.customer_name && <div className="flex justify-between"><span className="text-muted-foreground">Customer</span><span className="font-medium">{link.customer_name}</span></div>}
                {link.agent_name && <div className="flex justify-between"><span className="text-muted-foreground">Sent by</span><span className="font-medium">{link.agent_name}</span></div>}
                <div className="flex justify-between"><span className="text-muted-foreground">Reference</span><span className="font-mono text-xs">{link.code}</span></div>
              </div>
              {err && <div className="text-sm text-destructive text-center">{err}</div>}
              <Button
                onClick={startCheckout}
                disabled={processing}
                size="lg"
                className="w-full"
                data-testid="pay-link-pay-btn"
              >
                {processing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Redirecting…</> : <>Pay {money(link.amount_gbp)} securely</>}
              </Button>
              <div className="flex items-center gap-2 justify-center text-xs text-muted-foreground">
                <Lock className="w-3 h-3" /> Encrypted via Stripe · GDPR compliant · UK-registered
              </div>
            </CardContent>
          </Card>
        )}

        <div className="text-center mt-4 text-xs text-muted-foreground">
          By paying, you agree to Treasurescare's <a href="/terms" className="underline">Terms</a> and <a href="/privacy" className="underline">Privacy Policy</a>.
        </div>
      </div>
    </div>
  );
}
